import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    Field,
    makeStyles,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Switch,
    Text,
    tokens,
    Tooltip,
} from '@fluentui/react-components'
import {
    AddFilled,
    AddRegular,
    AlbumFilled,
    AlbumRegular,
    ArrowDownloadFilled,
    ArrowDownloadRegular,
    Building20Regular,
    BuildingFilled,
    BuildingRegular,
    bundleIcon,
    CallRegular,
    MailRegular,
    Person20Regular,
    PersonFilled,
    PersonRegular,
    ShieldPerson20Regular,
} from '@fluentui/react-icons'
import { useDispatch } from 'react-redux'
import useModules from '../../hooks/module/useModules'
import useRoles, { Role } from '../../hooks/useRoles'
import { AppAction } from '../../config/appConfig'
import { MenuItemKey } from '../../components/MainMenu'
import { useTranslation } from 'react-i18next'
import { Link, useHistory } from 'react-router-dom'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { ModuleType } from '../../context/GlobalContext'
import AttributesList from '../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../hooks/useAxios'
import useSubmitDocumentGenerationRequest from '../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { DocumentGenerationType } from '../../hooks/document/useDocumentGenerationHandler'
import { SortDirection } from '../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    parseFilterValuesToParams,
    useOverviewStyles,
} from '../BaseOverview/BaseOverview'
import { BaseOverviewFilterType } from '../BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import { isPartyOverviewItem, ListItemDto, PartyOverviewItemDto } from '../../config/partyConfig'
import { PartyStatus, PartyType } from '../../components/Form/Party/PartyForm'
import { Link as ReactLink } from '@fluentui/react-link'
import { composeEmailClick } from '../../components/List/FileParty/FilePartyList'
import IconValue from '../../components/IconValue/IconValue'
import useEntity from '../../hooks/useEntity'
import { IOverviewItemDto } from '../../config/fileConfig'
import BaseHeaderItem from '../BaseOverview/BaseHeaderItem'

const AlbumIcon = bundleIcon(AlbumFilled, AlbumRegular)

const useItemStyles = makeStyles({
    listPartyItem: {
        display: 'grid',
        gridTemplateColumns: '32px auto 1px 120px',
        minHeight: '24px',

        '& button[aria-label="hover"]': {
            display: 'none',
            marginLeft: tokens.spacingVerticalS,
            height: '24px',
        },

        '&:hover': {
            '& a, & a span': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },

            '& button[aria-label="hover"]': {
                display: 'inline-grid',
            },
        },
    },
    listPartyItemSmall: {
        gridTemplateColumns: '30px auto',
        gridTemplateRows: '1fr auto',
    },
    listPartyItemOverflow: {
        overflowWrap: 'anywhere',
    },
    listPartyItemTitle: {
        display: 'flex',

        '> *:first-child': {
            flexGrow: 1,
            flexShrink: 1,
        },

        '> *:not(:first-child)': {
            marginLeft: tokens.spacingVerticalS,
            flexGrow: 0,
        },
    },
    listPartyItemFileList: {
        gap: tokens.spacingVerticalS,
        margin: tokens.spacingVerticalS,
    },
    listPartyItemFileItem: {
        display: 'grid',
        gridTemplateColumns: 'minmax(60px, 80px) auto',
    },
    contactInfoButton: {
        width: '175px',
    },
    contactInfoText: {
        overflowWrap: 'anywhere',
    },
})

const AddIcon = bundleIcon(AddFilled, AddRegular)
const BuildingIcon = bundleIcon(BuildingFilled, BuildingRegular)
const DownloadIcon = bundleIcon(ArrowDownloadFilled, ArrowDownloadRegular)
const PersonIcon = bundleIcon(PersonFilled, PersonRegular)

type PartyFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        showNatural?: boolean
        showLegal?: boolean
        showAll?: boolean
    }

type PartySorting = {
    id: string
    displayName: string
    searchValue: string
}

type PartySortingType = BaseOverviewSortingType<PartySorting>

export type PartyLineItemProps = BaseOverviewLineItemProps<PartyOverviewItemDto, PartyFilterType, PartySortingType>

export function PartyFilter() {
    const { values, setFieldValue } = useFormikContext<PartyFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.Large) ? 'vertical' : 'horizontal'), [viewport])

    return (
        <>
            <Field label={translate('party.filter.natural.title')} orientation={orientation}>
                <Switch
                    checked={values.showNatural ?? true}
                    label={values.showNatural ? translate('base.toggle.boolean.true') : translate('base.toggle.boolean.false')}
                    onChange={(_, data) => {
                        setFieldValue('showNatural', data.checked)
                    }}
                />
            </Field>
            <Field label={translate('party.filter.legal.title')} orientation={orientation}>
                <Switch
                    checked={values.showLegal ?? true}
                    label={values.showLegal ? translate('base.toggle.boolean.true') : translate('base.toggle.boolean.false')}
                    onChange={(_, data) => {
                        setFieldValue('showLegal', data.checked)
                    }}
                />
            </Field>
            <Field label={translate('party.filter.inactive.title')} orientation={orientation}>
                <Switch
                    checked={values.showAll ?? false}
                    label={values.showAll ? translate('base.toggle.boolean.true') : translate('base.toggle.boolean.false')}
                    onChange={(_, data) => {
                        setFieldValue('showAll', data.checked)
                    }}
                />
            </Field>
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('party.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.Party}
                                context={[AttributeContextType.LegalPerson, AttributeContextType.NaturalPerson]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => setFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}

const headerSortingItems: PartySortingType[] = [
    {
        property: ['searchValue', 'id'],
        direction: SortDirection.Ascending,
        default: true,
        descriptionKey: 'sorting.az',
    },
    {
        property: ['searchValue', 'id'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.za',
    },
]

export function HeaderActionsButtons() {
    const history = useHistory()
    const overviewStyles = useOverviewStyles()
    const roles = useRoles()
    const { t: translate } = useTranslation()
    const { values } = useFormikContext<PartyFilterType>()

    const submitReportRequest = useSubmitDocumentGenerationRequest<PartyFilterType>(DocumentGenerationType.PartyXls)

    return (
        <>
            <Menu positioning={{ autoSize: true }}>
                <MenuTrigger disableButtonEnhancement>
                    <Tooltip withArrow relationship={'label'} content={translate('action.create.party.title')}>
                        <Button icon={<AddIcon />} appearance={'primary'} />
                    </Tooltip>
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        <MenuItem icon={<PersonIcon />} onClick={() => history.push('/party/natural/add')}>
                            {translate('action.create.person.natural.title')}
                        </MenuItem>
                        <MenuItem icon={<BuildingIcon />} onClick={() => history.push('/party/legal/add')}>
                            {translate('action.create.person.legal.title')}
                        </MenuItem>
                    </MenuList>
                </MenuPopover>
            </Menu>

            {roles.all([Role.Report]) && (
                <Tooltip withArrow relationship={'label'} content={translate('action.download.selection')}>
                    <Button icon={<DownloadIcon />} appearance={'secondary'} onClick={() => submitReportRequest(parseFilterValuesToParams(values))} />
                </Tooltip>
            )}
            <Button appearance={'secondary'} className={overviewStyles.hidden} />
        </>
    )
}

export const getPartyIcon = (party: ListItemDto): React.JSX.Element => {
    if (isPartyOverviewItem(party) && (party as PartyOverviewItemDto).identificationVerified) {
        return <ShieldPerson20Regular />
    }

    switch (party.type) {
        case PartyType.Legal: {
            return <Building20Regular />
        }
        case PartyType.Natural: {
            return <Person20Regular />
        }
    }

    return undefined
}

const getUpdateLink = (party: PartyOverviewItemDto): string => {
    switch (party.type) {
        case PartyType.Legal: {
            return `/party/legal/${party.id}/edit`
        }
        case PartyType.Natural:
        default: {
            return `/party/natural/${party.id}/edit`
        }
    }
}

function PartyListItemTitle(props: PartyLineItemProps) {
    const partyStyles = useItemStyles()
    const { t: translate } = useTranslation()
    const [showMenu, setShowMenu] = useState<boolean>(false)

    const [files, , resetFiles] = useEntity<IOverviewItemDto[]>(
        () => ({ url: '/file', params: { parties: [props.item.id] } }),
        showMenu ? false : undefined,
        result => result.data,
        undefined,
        undefined,
        [showMenu],
    )

    return (
        <div className={partyStyles.listPartyItemTitle}>
            <Link
                key={props.item.id}
                to={getUpdateLink(props.item) || '/party'}
                className={props.item.status === PartyStatus.Active ? 'party item-open' : 'party item-closed'}
            >
                <Text className={partyStyles.listPartyItemOverflow}>{props.item.displayName || <em>Naam ontbreekt.</em>}</Text>
            </Link>

            <Menu
                positioning={{ autoSize: true }}
                open={showMenu}
                onOpenChange={(_, { open }) => {
                    setShowMenu(open)
                    if (!open) {
                        resetFiles()
                    }
                }}
            >
                <MenuTrigger disableButtonEnhancement>
                    <Tooltip relationship={'label'} content={translate('party.file.dialog')} withArrow>
                        <Button aria-label={'hover'} icon={<AlbumIcon />} appearance="secondary" size={'small'} />
                    </Tooltip>
                </MenuTrigger>

                <MenuPopover>
                    <MenuList className={partyStyles.listPartyItemFileList}>
                        {showMenu && files.loaded && files.data?.length === 0 && <div key={-1}>{translate('base.empty')}</div>}
                        {showMenu &&
                            files.data?.map((file, index) => (
                                <div key={index} className={partyStyles.listPartyItemFileItem}>
                                    <Link to={`/file/${file.id}`}>
                                        <Text size={200}>{file.reference}</Text>
                                    </Link>
                                    <Text size={200}>{file.name.substring(0, 50)}</Text>
                                </div>
                            ))}
                    </MenuList>
                </MenuPopover>
            </Menu>
        </div>
    )
}

export function PartyListItem(props: BaseOverviewLineItemProps<PartyOverviewItemDto, PartyFilterType, PartySortingType>) {
    const overviewStyles = useOverviewStyles()
    const itemStyles = useItemStyles()

    return (
        <>
            <div>{getPartyIcon(props.item)}</div>
            <PartyListItemTitle {...props} />
            <div></div>
            <div>
                {props.item.email && (
                    <IconValue
                        value={
                            <ReactLink onClick={composeEmailClick(props.item)} className={itemStyles.contactInfoButton}>
                                <Text size={200} className={itemStyles.contactInfoText}>
                                    {props.item.email}
                                </Text>
                            </ReactLink>
                        }
                        icon={<MailRegular />}
                        textVariant="small"
                        wrapperClassName={overviewStyles.alignSelfStart}
                    />
                )}
                {props.item.phoneNumber && (
                    <IconValue
                        value={
                            <ReactLink href={`tel:${props.item.phoneNumber}`}>
                                <Text size={200}>{props.item.phoneNumber}</Text>
                            </ReactLink>
                        }
                        icon={<CallRegular />}
                        textVariant="small"
                        wrapperClassName={overviewStyles.alignSelfStart}
                    />
                )}
            </div>
        </>
    )
}

export default function PartyOverview() {
    const axios = useAxios()
    const dispatch = useDispatch()
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])

    //Reset breadcrumbs and data
    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Parties] })
    }, [])

    const defaultFilterValues = useMemo<PartyFilterType>(
        () => ({
            showNatural: true,
            showLegal: true,
            showAll: false,
            attributes: [],
            query: '',
        }),
        [],
    )

    const countItems = (column?: number, filterValues?: PartyFilterType): Promise<BaseOverviewCount> => {
        return axios.get('/party/count', { params: parseFilterValuesToParams(filterValues) }).then(response => response.data)
    }

    const loadItems = (
        column?: number,
        filterValues?: PartyFilterType,
        sortingValues?: PartySortingType,
        start?: number,
        size?: number,
    ): Promise<PartyOverviewItemDto[]> => {
        if ((sortingValues?.property?.[0] as string) === 'displayName') {
            sortingValues = { ...sortingValues, property: ['searchValue', 'id'] }
        }

        return axios
            .get('/party', { params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) } })
            .then((response: AxiosResponse<PartyOverviewItemDto[]>) => response?.data || [])
    }

    return (
        <BaseOverview<PartyOverviewItemDto, PartyFilterType, PartySortingType>
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listItemHeaderContent={BaseHeaderItem}
            listItemHeaderClassnames={[overviewStyles.listBaseHeaderItem]}
            listItemContent={props => <PartyListItem {...props} />}
            listItemClassnames={() => [itemStyles.listPartyItem, listItemSmall && itemStyles.listPartyItemSmall]}
            headerActionsButtons={HeaderActionsButtons}
            headerSortingItems={headerSortingItems}
            filterContent={PartyFilter}
            inputPlaceholderKey={'party.filter.search'}
            localStorageKeyPrefix={'HammockPartyOverview'}
        />
    )
}
